import { useState, useEffect } from "react";
import { Plus, Pencil, Trash2, ExternalLink, Check, Gift, Star } from "lucide-react";
import { toast } from "sonner";
import { api, apiErr, fileUrl } from "@/lib/api";
import { Button } from "@/components/Button";
import { Modal, Field, TextInput, TextArea } from "@/components/Modal";
import { ImageUpload } from "@/components/ImageUpload";
import { EmptyState, Loader } from "@/components/common";

const CATEGORIES = ["Clothing", "Beauty", "Electronics", "Home", "Food", "Hobbies", "Kids", "Experiences", "Other"];
const PRIORITIES = [["low", "Low"], ["medium", "Medium"], ["high", "High"]];

function PriorityDots({ level }) {
  const n = level === "high" ? 3 : level === "medium" ? 2 : 1;
  return (
    <span className="inline-flex gap-0.5">
      {[1, 2, 3].map((i) => (
        <Star key={i} size={12} className={i <= n ? "text-sky-400 fill-sky-400" : "text-slate-700"} />
      ))}
    </span>
  );
}

export function WishlistView({ connectionId = null, shared = false }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [filterCat, setFilterCat] = useState("All");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/wishlist", { params: connectionId ? { connection_id: connectionId } : {} });
      setItems(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [connectionId]);

  const startAdd = () => {
    setEditing(null);
    setForm({ name: "", link: "", photo: null, price: "", notes: "", priority: "medium", category: "Other" });
    setOpen(true);
  };
  const startEdit = (it) => {
    setEditing(it);
    setForm({ ...it, price: it.price ?? "" });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name?.trim()) return toast.error("Please enter an item name.");
    setBusy(true);
    try {
      const payload = {
        name: form.name, link: form.link || "", photo: form.photo || null,
        price: form.price === "" ? null : Number(form.price), notes: form.notes || "",
        priority: form.priority, category: form.category,
      };
      if (editing) await api.put(`/wishlist/${editing.id}`, payload);
      else await api.post("/wishlist", { ...payload, connection_id: connectionId });
      setOpen(false);
      await load();
      toast.success("Saved");
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const del = async (it) => {
    await api.delete(`/wishlist/${it.id}`);
    await load();
  };
  const togglePurchased = async (it) => {
    await api.put(`/wishlist/${it.id}`, { purchased: !it.purchased });
    await load();
  };
  const reserve = async (it) => {
    await api.post(`/wishlist/${it.id}/reserve`);
    await load();
  };

  const shown = filterCat === "All" ? items : items.filter((i) => i.category === filterCat);

  if (loading) return <Loader />;

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1">
          {["All", ...CATEGORIES].map((c) => (
            <button
              key={c}
              onClick={() => setFilterCat(c)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                filterCat === c ? "bg-blue-600 text-white" : "bg-slate-800 text-slate-400 hover:text-slate-200"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
        <Button onClick={startAdd} data-testid="add-wishlist-btn" className="!py-2 shrink-0"><Plus size={16} /> Add</Button>
      </div>

      {shown.length === 0 ? (
        <EmptyState
          icon={Gift}
          title={shared ? "No shared wishes yet" : "Your wishlist is empty"}
          subtitle={shared ? "Add things you both want — home items, trips, date ideas." : "Save things you'd love to receive. Add links from anywhere."}
          action={<Button onClick={startAdd}><Plus size={16} /> Add item</Button>}
          testId="wishlist-empty"
        />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {shown.map((it) => (
            <div key={it.id} className="iks-card overflow-hidden group" data-testid="wishlist-item">
              {it.photo && <img src={fileUrl(it.photo)} alt="" className="w-full h-40 object-cover" />}
              <div className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className={`font-medium text-slate-100 ${it.purchased ? "line-through text-slate-500" : ""}`}>{it.name}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-slate-500">{it.category}</span>
                      <PriorityDots level={it.priority} />
                    </div>
                  </div>
                  {it.price != null && <span className="text-sky-400 font-semibold text-sm shrink-0">${Number(it.price).toFixed(2)}</span>}
                </div>
                {it.notes && <p className="text-slate-400 text-sm mt-2 whitespace-pre-line break-words">{it.notes}</p>}
                <div className="flex items-center gap-2 mt-3 flex-wrap">
                  {it.link && (
                    <a href={/^https?:\/\//i.test(it.link) ? it.link : `https://${it.link}`} target="_blank" rel="noopener noreferrer" className="text-xs text-sky-400 inline-flex items-center gap-1 hover:text-sky-300">
                      <ExternalLink size={13} /> View
                    </a>
                  )}
                  {shared ? (
                    <button
                      onClick={() => reserve(it)}
                      data-testid="reserve-btn"
                      className={`text-xs px-2.5 py-1 rounded-lg font-medium transition-colors ${
                        it.reserved_by ? "bg-emerald-500/15 text-emerald-400" : "bg-slate-800 text-slate-300 hover:bg-slate-700"
                      }`}
                    >
                      {it.reserved_by ? "Reserved" : "I got this"}
                    </button>
                  ) : (
                    <button onClick={() => togglePurchased(it)} className="text-xs text-slate-400 inline-flex items-center gap-1 hover:text-emerald-400" data-testid="purchased-toggle">
                      <Check size={13} /> {it.purchased ? "Purchased" : "Mark purchased"}
                    </button>
                  )}
                  <div className="ml-auto flex gap-1 opacity-60 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => startEdit(it)} className="p-1.5 text-slate-400 hover:text-sky-400"><Pencil size={14} /></button>
                    <button onClick={() => del(it)} className="p-1.5 text-slate-400 hover:text-red-400" data-testid="delete-wishlist-btn"><Trash2 size={14} /></button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal
        open={open} onClose={() => setOpen(false)} title={editing ? "Edit item" : "Add to wishlist"} testId="wishlist-modal"
        footer={
          <>
            <Button variant="secondary" full onClick={() => setOpen(false)}>Cancel</Button>
            <Button full loading={busy} onClick={save} data-testid="save-wishlist-btn">Save</Button>
          </>
        }
      >
        <Field label="Item name"><TextInput value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="What do you want?" data-testid="wishlist-name" /></Field>
        <Field label="Link (Amazon, TikTok, anywhere)"><TextInput value={form.link} onChange={(e) => setForm((f) => ({ ...f, link: e.target.value }))} placeholder="https://" /></Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3">
          <Field label="Price"><TextInput type="number" step="0.01" value={form.price} onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))} placeholder="0.00" /></Field>
          <Field label="Priority">
            <select value={form.priority} onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))} className="iks-input w-full px-3.5 py-2.5 text-sm">
              {PRIORITIES.map(([v, l]) => <option key={v} value={v} className="bg-slate-900">{l}</option>)}
            </select>
          </Field>
        </div>
        <Field label="Category">
          <select value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} className="iks-input w-full px-3.5 py-2.5 text-sm" data-testid="wishlist-category">
            {CATEGORIES.map((c) => <option key={c} value={c} className="bg-slate-900">{c}</option>)}
          </select>
        </Field>
        <Field label="Notes"><TextArea value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} placeholder="Size, color, details…" /></Field>
        <Field label="Photo (optional)"><ImageUpload variant="wide" value={form.photo} onChange={(p) => setForm((f) => ({ ...f, photo: p }))} /></Field>
      </Modal>
    </div>
  );
}
